import { ChevronDown } from '@untitledui/icons';
import { type FC, type ReactNode, type Ref, type RefAttributes } from 'react';
import { isValidElement } from 'react';
import type {
  SelectProps as AriaSelectProps,
  SelectRenderProps,
  SelectValueRenderProps,
} from 'react-aria-components';
import {
  Button as AriaButton,
  ListBox as AriaListBox,
  Select as AriaSelect,
  SelectValue as AriaSelectValue,
} from 'react-aria-components';

import { Avatar } from '@/lib/base/avatar/avatar';
import { ComboBox } from '@/lib/base/forms/combobox';
import { HintText } from '@/lib/base/forms/hint-text';
import { Label } from '@/lib/base/forms/input/label';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import {
  type CommonProps,
  SelectContext,
  type SelectItemType,
  type SelectSize,
  sizes,
} from '@/lib/base/forms/select/select-shared';
import { Popover } from '@/lib/base/overlays/popover';
import { cx } from '@/utils/cx';
import { isReactComponent } from '@/utils/is-react-component';

// `SelectContext`, `sizes`, and these shared types are defined in the
// dependency-free `./select-shared` leaf to avoid an import cycle back into
// this module (see select-shared.ts). Re-exported here so external importers
// of these symbols from `./select` keep working unchanged.
export type { CommonProps, SelectItemType, SelectSize };
// eslint-disable-next-line react-refresh/only-export-components -- re-export of shared constants/context, not components
export { SelectContext, sizes };

interface SelectProps
  extends
    Omit<AriaSelectProps<SelectItemType>, 'children' | 'items'>,
    RefAttributes<HTMLDivElement>,
    CommonProps {
  items?: SelectItemType[];
  popoverClassName?: string;
  /** Override the popover placement (default: 'bottom'). Use 'bottom end' to right-align. */
  popoverPlacement?: 'bottom' | 'bottom start' | 'bottom end';
  placeholderIcon?: FC | ReactNode;
  children: ReactNode | ((item: SelectItemType) => ReactNode);
  /** Controls whether error styling is shown. When false, suppresses error UI even if isInvalid is true. */
  showError?: boolean;
  /**
   * Extra classes merged onto the trigger button AFTER the base styling
   * (tailwind-merge, so overrides win). For embedding the select inside a
   * composed control — e.g. the currency input's compact trailing select
   * inside an InputGroup — where the trigger must drop its own ring/shadow
   * and inherit the group's chrome.
   */
  triggerClassName?: string;
}

interface SelectValueProps {
  isOpen: boolean;
  size: SelectSize;
  isFocused: boolean;
  isDisabled: boolean;
  placeholder?: string;
  ref?: Ref<HTMLButtonElement>;
  placeholderIcon?: FC | ReactNode;
  triggerClassName?: string;
}

// Chevron sizing per select size. `md` scales up at the `sm` breakpoint to
// match its responsive root sizing, so mobile responsiveness is built in.
// eslint-disable-next-line react-refresh/only-export-components -- constant co-located with component
export const CHEVRON_SIZE_CLASSES: Record<SelectSize, string> = {
  xs: 'size-4 stroke-[2.5px]',
  sm: 'size-4 stroke-[2.5px]',
  md: 'size-4 stroke-[2.5px] sm:size-5 sm:stroke-2',
  lg: 'size-5 stroke-2',
  xl: 'size-5 stroke-2',
};

// Base classes for the trigger button — shared with domain selects that
// hand-roll their dropdown (e.g. PropertyMappingSelect) but must match the
// library Select trigger's look and feel.
export const SELECT_TRIGGER_BASE =
  'relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset';
export const SELECT_TRIGGER_FOCUS = 'ring-2 ring-[var(--color-focus-ring)]';

const SelectValue = ({
  isOpen,
  isFocused,
  isDisabled,
  size,
  placeholder,
  placeholderIcon,
  ref,
  triggerClassName,
}: SelectValueProps) => {
  return (
    <AriaButton
      ref={ref}
      // Marks the library Select's trigger so composite fields (InputGroup)
      // can style around it the way they do around a native <select> —
      // :has(select) can't see a button-based trigger.
      data-select-trigger=''
      className={cx(
        SELECT_TRIGGER_BASE,
        (isFocused || isOpen) && SELECT_TRIGGER_FOCUS,
        isDisabled && 'cursor-not-allowed bg-disabled_subtle text-disabled',
        triggerClassName
      )}
    >
      <AriaSelectValue<SelectItemType>
        className={cx(
          'flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle',

          // Icon styles — dimensions come from the shared scale so the glyph
          // shrinks with the box (a 20px icon swamps the 32px `xs` control).
          '*:data-icon:shrink-0 *:data-icon:text-fg-quaternary in-disabled:*:data-icon:text-fg-disabled',
          sizes[size].iconSlot,

          sizes[size].root
        )}
      >
        {(state: SelectValueRenderProps<SelectItemType>) => {
          const Icon = state.selectedItem?.icon || placeholderIcon;

          let leadingIcon: ReactNode = null;
          if (state.selectedItem?.avatarUrl) {
            leadingIcon = (
              <Avatar
                size='xs'
                src={state.selectedItem.avatarUrl}
                alt={state.selectedItem.label}
              />
            );
          } else if (isReactComponent(Icon)) {
            leadingIcon = <Icon data-icon aria-hidden='true' />;
          } else if (isValidElement(Icon)) {
            leadingIcon = Icon;
          }

          return (
            <>
              {leadingIcon}

              {state.selectedItem ? (
                <section className='flex w-full gap-2 truncate'>
                  {/* Weight is deliberately normal: filled-vs-empty is already
                      signalled by color (text-primary vs text-placeholder), so
                      weight stays free for real emphasis and a value reads the
                      same here as it does typed into an Input. */}
                  <p className='truncate text-primary'>
                    {state.selectedItem?.label}
                  </p>
                  {state.selectedItem?.supportingText && (
                    <p className='text-tertiary'>
                      {state.selectedItem?.supportingText}
                    </p>
                  )}
                </section>
              ) : (
                <p
                  className={cx(
                    'text-placeholder',
                    isDisabled && 'text-disabled'
                  )}
                >
                  {placeholder}
                </p>
              )}

              <ChevronDown
                aria-hidden='true'
                className={cx(
                  'ml-auto shrink-0 text-fg-quaternary',
                  CHEVRON_SIZE_CLASSES[size]
                )}
              />
            </>
          );
        }}
      </AriaSelectValue>
    </AriaButton>
  );
};

const Select = ({
  placeholder = 'Select',
  placeholderIcon,
  size = 'sm',
  children,
  items,
  label,
  hint,
  tooltip,
  showError,
  popoverPlacement,
  className,
  triggerClassName,
  ...rest
}: SelectProps) => {
  const effectiveIsInvalid = (showError ?? true) && !!rest.isInvalid;

  return (
    <SelectContext.Provider value={{ size }}>
      <AriaSelect
        {...rest}
        isInvalid={effectiveIsInvalid}
        className={(state: SelectRenderProps) =>
          cx(
            'flex flex-col gap-1.5',
            typeof className === 'function'
              ? className(state as never)
              : className
          )
        }
      >
        {(state: SelectRenderProps) => (
          <>
            {label && (
              <Label isRequired={state.isRequired} tooltip={tooltip}>
                {label}
              </Label>
            )}

            <SelectValue
              {...state}
              {...{ size, placeholder }}
              placeholderIcon={placeholderIcon}
              triggerClassName={triggerClassName}
            />

            <Popover
              size={size}
              className={rest.popoverClassName}
              placement={popoverPlacement ?? 'bottom'}
            >
              <AriaListBox items={items} className='size-full outline-hidden'>
                {children}
              </AriaListBox>
            </Popover>

            {hint && (
              <HintText isInvalid={state.isInvalid} showError={showError}>
                {hint}
              </HintText>
            )}
          </>
        )}
      </AriaSelect>
    </SelectContext.Provider>
  );
};

const _Select = Select as typeof Select & {
  ComboBox: typeof ComboBox;
  Item: typeof SelectItem;
};
_Select.ComboBox = ComboBox;
_Select.Item = SelectItem;

export { _Select as Select };
